/** /t/live/<sessionId> — the teacher's control view, rebuilt 2026-09-10 to the revised design
 * (docs/ux-design-live-view-2026-09-10.md) after the owner taught a lesson with the old one.
 *
 * The screen is a **stage** and a **deck**. The stage has exactly one hero and the hero changes
 * with the moment; the deck is Next and Asked; the action bar is fixed to the foot of the stage
 * column and is the control surface and the shortcut documentation at once. Quiet mode is the
 * default and governs what sits beside the hero, never what is reachable.
 *
 * This file is composition. The parts live in `src/live/`: the stage and its faces, the bar,
 * the deck, the room panel, the ask-aloud composer, post-hoc scoring, the shortcut layer.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { APP_BASE } from "../api/base";
import { useCardsEnabled } from "../cards/enabled";
import { accessToken } from "../auth/session";
import { deviceId, nextSeq } from "../live/device";
import { useLive, type AskedEntry } from "../live/useLive";
import { createItem, diagnose, getCourse, getItems, getSession, listPlans, lockSession, releaseSession, setItemReview, type Diagnosis, type Item } from "../teacher/api";
import { capabilities } from "../platform/capabilities";
import { useJoinOrigin } from "../platform/joinOrigin";
import { TeacherChrome } from "../teacher/Chrome";
import { Button, StatusBar, useConfirm } from "../ui";
import { SuggestionCard } from "../teacher/SuggestionCard";
import { ActionBar, type BarAction } from "../live/ActionBar";
import { AskAloudSheet, type Shape } from "../live/AskAloudSheet";
import { CardScanSheet, useCardScan } from "../live/CardScanSheet";
import { scanOffered } from "../live/cardScan";
import { LiveStage, type Face } from "../live/LiveStage";
import { QuestionDeck } from "../live/QuestionDeck";
import { RefusalLine } from "../live/RefusalLine";
import { RoomPanel } from "../live/RoomPanel";
import { ScoreAloudSheet } from "../live/ScoreAloudSheet";
import { ShortcutSheet } from "../live/ShortcutSheet";
import { useLocalHost } from "../live/host";
import { useDeckBeside } from "../live/layout";
import { useDevicePref } from "../live/prefs";
import { buildDeck, canRevote, canScore, keepSelection, moveSelection, nextAttempt } from "../live/queue";
import type { LiveAction, KeyContext } from "../live/shortcuts";
import { focusStage, useLiveShortcuts } from "../live/useLiveShortcuts";

export function Live() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("ru") ? "ru" : "en";
  const confirm = useConfirm();
  const dev = useMemo(() => deviceId(), []);
  const token = accessToken() ?? "dev";
  const qc = useQueryClient();

  const session = useQuery({ queryKey: ["session", sessionId], queryFn: () => getSession(sessionId) });
  const courseId = session.data?.course_id;
  const items = useQuery({ queryKey: ["items", courseId], queryFn: () => getItems(courseId!), enabled: !!courseId });
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId!), enabled: !!courseId });
  const lessonId = session.data?.lesson_id ?? null;
  const plans = useQuery({ queryKey: ["plans", lessonId], queryFn: () => listPlans(lessonId!), enabled: !!lessonId });
  const plan = plans.data?.find((p) => p.status === "approved") ?? null;
  const loCode = useCallback(
    (loId: string | null | undefined) => course.data?.los.find((l) => l.lo_id === loId)?.code ?? null,
    [course.data],
  );
  const release = useMutation({
    mutationFn: () => releaseSession(sessionId),
    onSuccess: (s) => qc.setQueryData(["session", sessionId], s),
  });

  const { snapshot, conn, last, send, skewMs } = useLive(sessionId, token, dev);
  const item = snapshot?.item ?? null;
  const agg = snapshot?.aggregate ?? null;
  const state = snapshot?.state ?? "lobby";
  const open = state === "item_open";
  const closed = state === "item_closed" || state === "intervention";
  /** The lesson is over. The engine refuses every command but `release_summary` and
   * `score_item` from here on (session_engine.engine.handle), so every control that would
   * send one of the others is disabled on the screen rather than left to be refused. */
  const ended = state === "ended";
  const present = snapshot?.present ?? 0;
  const asked: AskedEntry[] = useMemo(() => snapshot?.asked ?? [], [snapshot?.asked]);
  const live = conn === "live";

  // --- per-device view state (never syncs, never changes what a student sees) ---------------
  const [quiet, setQuiet] = useDevicePref("quiet", true);
  const [keysOn, setKeysOn] = useDevicePref("keys", true);
  const [timerS, setTimerS] = useDevicePref("timer", 30);
  const [skipped, setSkipped] = useDevicePref<string[]>("skipped", []);
  const [tipDone, setTipDone] = useDevicePref("tipDone", false);
  const [deckOpen, setDeckOpen] = useState(false);
  const [tab, setTab] = useState<"next" | "asked">("next");
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showRoom, setShowRoom] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [aloudOpen, setAloudOpen] = useState(false);
  const [keysSheet, setKeysSheet] = useState(false);
  const [scoring, setScoring] = useState<AskedEntry | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [draft, setDraft] = useState<Diagnosis | null>(null);
  const [locked, setLocked] = useState<boolean | null>(null);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [kept, setKept] = useState<string[]>([]);
  const [keeping, setKeeping] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  /** Printed cards (STORY-089): the scan is offered where cards are on and the browser can
   * take a photo, for an item a card can carry. */
  const cardsOn = useCardsEnabled();

  const menu = useRef<HTMLDetailsElement>(null);
  const scope = useRef<HTMLDivElement>(null);
  const [scopeEl, setScopeEl] = useState<HTMLDivElement | null>(null);
  const beside = useDeckBeside(scopeEl);

  // --- the deck ------------------------------------------------------------------------------
  const deck = useMemo(
    () => buildDeck(items.data ?? [], plan?.plan.segments ?? null, asked, skipped, search),
    [items.data, plan, asked, skipped, search],
  );
  const index = useRef(0);
  useEffect(() => {
    const next = keepSelection(deck.next, selected, index.current);
    if (next !== selected) setSelected(next);
    index.current = Math.max(0, deck.next.findIndex((r) => r.item.item_id === next));
  }, [deck.next, selected]);
  const selectedItem = deck.next.find((r) => r.item.item_id === selected) ?? null;

  // --- commands ------------------------------------------------------------------------------
  const inFlight = useRef(false);
  const firedAt = useRef(0);
  const closedAt = useRef(0);
  const cmd = useCallback(
    (name: string, args: Record<string, unknown> = {}) => {
      inFlight.current = true;
      window.setTimeout(() => (inFlight.current = false), 2000);
      send({ cmd: name, seq: nextSeq(`host:${sessionId}`), args });
    },
    [send, sessionId],
  );
  useEffect(() => {
    inFlight.current = false;
    if (closed) closedAt.current = Date.now();
  }, [state, item?.item_id, item?.attempt, closed]);

  const snapshotOf = (it: Item) => ({
    item_id: it.item_id,
    type: it.type,
    stem: it.stem,
    options: it.options,
    answer_spec: it.answer_spec,
    confidence_enabled: it.confidence_enabled,
    lo_id: it.lo_id,
    lang: it.lang,
    spoken: !!it.spoken,
  });

  const ask = useCallback(
    (it: Item) => {
      firedAt.current = Date.now();
      cmd("open_item", { item_snapshot: snapshotOf(it), timer_s: timerS || null, basis: "first" });
    },
    [cmd, timerS],
  );

  const approveAndAsk = useMutation({
    mutationFn: (it: Item) => setItemReview(it.item_id, "approved"),
    onMutate: (it: Item) => setBusyItemId(it.item_id),
    onSettled: () => setBusyItemId(null),
    onSuccess: (it) => {
      void qc.invalidateQueries({ queryKey: ["items", courseId] });
      ask(it);
    },
  });
  const askOrApprove = useCallback(
    (it: Item) => (it.review_status === "draft" ? approveAndAsk.mutate(it) : ask(it)),
    [approveAndAsk, ask],
  );

  /** Ask the same question again: a new round, scored on its own, at the next free attempt.
   * `basis: "fresh"` is what stops the engine reading a before/after into it (§8.3). */
  const askAgain = useCallback(
    (entry: AskedEntry) => {
      const bank = (items.data ?? []).find((it) => it.item_id === entry.item_id);
      const snap = bank
        ? snapshotOf(bank)
        : {
            item_id: entry.item_id,
            type: entry.type,
            stem: entry.stem,
            options: entry.option_keys.map((key) => ({ key, label: "" })),
            answer_spec: {},
            confidence_enabled: false,
            lo_id: entry.lo_id,
            lang,
            spoken: entry.spoken,
          };
      firedAt.current = Date.now();
      cmd("open_item", {
        item_snapshot: snap,
        timer_s: timerS || null,
        attempt: nextAttempt(asked, entry.item_id),
        basis: "fresh",
      });
      setDeckOpen(false);
      focusStage();
    },
    [asked, cmd, items.data, lang, timerS],
  );

  const askAloud = useCallback(
    (v: { keys: string[]; shape: Shape; loId: string | null; timerS: number; note: string }) => {
      firedAt.current = Date.now();
      setTimerS(v.timerS);
      cmd("open_item", {
        item_snapshot: {
          item_id: `aloud-${Date.now().toString(36)}`,
          type: "single",
          // the note is the stem; the server blanks a spoken item's stem for students
          stem: v.note || t(`live.quick.${v.shape}`),
          options: v.keys.map((key) => ({ key, label: "" })),
          answer_spec: {},
          confidence_enabled: false,
          lo_id: v.loId,
          lang,
          spoken: true,
        },
        timer_s: v.timerS || null,
        basis: "first",
      });
      setAloudOpen(false);
      focusStage();
    },
    [cmd, lang, setTimerS, t],
  );

  /** Keep an ask-aloud question in the bank — offered afterwards, from the Asked row, where
   * the teacher already knows whether the question was any good (owner decision 3). */
  const keep = useCallback(
    async (entry: AskedEntry) => {
      if (!courseId) return;
      setKeeping(entry.item_id);
      try {
        await createItem(courseId, {
          type: "single",
          stem: entry.stem || t(`live.quick.abcd`),
          options: entry.option_keys.map((key) => ({ key, label: "", correct: (entry.correct_keys ?? []).includes(key) })),
          answer_spec: {},
          confidence_enabled: false,
          lo_id: entry.lo_id,
          lang,
          spoken: true,
        });
        await qc.invalidateQueries({ queryKey: ["items", courseId] });
        setKept((k) => [...k, entry.item_id]);
      } finally {
        setKeeping(null);
      }
    },
    [courseId, lang, qc, t],
  );

  // --- the AI diagnosis of the most-chosen wrong option (unchanged) --------------------------
  const closedKey = closed && item ? `${item.item_id}:${item.attempt}` : "";
  useEffect(() => {
    if (!closedKey || !item || !agg || agg.answered < 3) {
      setDraft(null);
      return;
    }
    let cancelled = false;
    diagnose({
      item_snapshot: item.snapshot,
      options: agg.options.map((o) => ({ key: o.key, count: o.count, correct: o.correct, misconception: o.misconception })),
      answered: agg.answered,
      lang,
      budget_s: 3,
    })
      .then((d) => !cancelled && setDraft(d))
      .catch(() => !cancelled && setDraft(null));
    return () => {
      cancelled = true;
    };
  }, [closedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- what the screen is showing --------------------------------------------------------------
  const isLocked = locked ?? session.data?.locked ?? false;
  const hostDevice = snapshot?.host_device_id ?? session.data?.host_device_id ?? null;
  const iAmHost = hostDevice === dev;
  const joinOrigin = useJoinOrigin();
  const joinUrl = session.data && joinOrigin ? `${joinOrigin}${APP_BASE}${session.data.join_path}` : "";
  /** The Teacher Computer app hosts this session with no internet, so a chat channel that
   * answers over the internet is off for this lesson and the poster says so (FR-056). */
  const localHost = useLocalHost();
  /** Anonymous join mode: the roster may state a channel as a count and mark no row (FR-051). */
  const anonymous = session.data?.join_mode === "anonymous";
  /** The card scan's round: the open item, or none. The scan's controller lives here, not in its
   * sheet, so closing the sheet loses no answer still queued. */
  const scanItem = open && item ? { item_id: item.item_id, attempt: item.attempt, snapshot: item.snapshot } : null;
  const scan = useCardScan(
    cardsOn && capabilities.cardCamera && session.data
      ? { sessionId, courseId: session.data.course_id, joinCode: session.data.join_code, anonymous }
      : null,
    { item: scanItem, skewMs, labels: snapshot?.labels, connected: live },
  );
  const projectorUrl = session.data
    ? `${APP_BASE}/p/${sessionId}?token=${encodeURIComponent(session.data.projector_token)}&code=${session.data.join_code}`
    : "";
  const hasKey = !!agg && (agg.options.some((o) => o.correct) || !!agg.correct_keys?.length);

  const face: Face = session.isPending
    ? "loading"
    : state === "ended"
      ? "ended"
      : posterOpen || (state === "lobby" && present === 0)
        ? "poster"
        : open
          ? "asking"
          : closed
            ? "result"
            : deck.next.length > 0
              ? "upNext"
              : "empty";

  /** The projector is the second window — the one the room looks at. A teacher presses this
   * twice: once to open it, then to bring it back in front of the slides. Opening it, finding
   * it again and knowing whether it is still up all live in `capabilities.projectorWindow`,
   * which is the only place that may hold a second window: the desktop webview implements no
   * `window.open` at all. */
  const [projectorUp, setProjectorUp] = useState(false);
  const openProjector = useCallback(() => {
    if (!session.data) return;
    // A blocked pop-up must not leave the button claiming it can focus one.
    void capabilities.projectorWindow.open(sessionId, projectorUrl).then(setProjectorUp);
  }, [projectorUrl, session.data, sessionId]);
  useEffect(() => {
    // A closed window fires nothing we can hear, so the label is corrected on a slow tick and
    // only while it claims to be open.
    if (!projectorUp || capabilities.projectorWindow.native) return;
    const id = window.setInterval(() => {
      if (!capabilities.projectorWindow.isOpen()) setProjectorUp(false);
    }, 3000);
    return () => window.clearInterval(id);
  }, [projectorUp]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const el = menu.current;
      if (el?.open && e.target instanceof Node && !el.contains(e.target)) el.removeAttribute("open");
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  /** One line, four seconds. Counted rather than compared: two refusals with the same words
   * (three keys in a row after the session ends) used to cancel each other, and the second one
   * left the line blank — the one thing this line may never be. */
  const saidAt = useRef(0);
  const say = useCallback((key: string) => {
    const at = ++saidAt.current;
    setRefusal(key);
    window.setTimeout(() => {
      if (saidAt.current === at) setRefusal(null);
    }, 4000);
  }, []);

  // --- the key layer -----------------------------------------------------------------------
  const context = useCallback(
    (): KeyContext => ({
      live,
      state: (state === "lobby" || state === "item_open" || state === "item_closed" || state === "intervention" || state === "ended" ? state : "lobby") as KeyContext["state"],
      queued: deck.next.length > 0,
      selection: !!selectedItem,
      answered: (agg?.answered ?? 0) > 0,
      keyed: hasKey,
      explanation: !!draft?.explanation,
      remote: !iAmHost,
      deckOpen,
      tab,
      inFlight: inFlight.current,
      now: Date.now(),
      firedAt: firedAt.current,
      closedAt: closedAt.current,
    }),
    [agg?.answered, deck.next.length, deckOpen, draft?.explanation, hasKey, iAmHost, live, selectedItem, state, tab],
  );

  const primaryAction = useCallback(() => {
    firedAt.current = Date.now();
    if (!tipDone) setTipDone(true);
    if (open) {
      cmd("close_item");
      return;
    }
    if (selectedItem) askOrApprove(selectedItem.item);
    else setAloudOpen(true);
  }, [askOrApprove, cmd, open, selectedItem, setTipDone, tipDone]);

  const perform = useCallback(
    (a: LiveAction) => {
      switch (a.kind) {
        case "ask":
        case "close":
          primaryAction();
          break;
        case "aloud":
          setAloudOpen(true);
          break;
        case "extend":
          cmd("extend", { seconds: a.seconds });
          break;
        case "setTimer":
          setTimerS(a.seconds);
          say("live.coach.timerSet");
          break;
        case "reveal":
          cmd("reveal", { mode: item?.revealed === a.mode ? "nothing" : a.mode });
          break;
        case "revote":
          firedAt.current = Date.now();
          cmd("revote", { timer_s: 90 });
          break;
        case "skip":
          if (selected) setSkipped([...skipped, selected]);
          break;
        case "move":
          setDeckOpen(true);
          setTab("next");
          setSelected(moveSelection(deck.next, selected, a.delta));
          break;
        case "tab":
          setDeckOpen(true);
          setTab(a.to);
          break;
        case "room":
          setShowRoom((v) => !v);
          break;
        case "quiet":
          setQuiet(!quiet);
          break;
        case "projector":
          openProjector();
          break;
        case "explain":
          setQuiet(false);
          break;
        case "takeover":
          cmd("handoff", { new_host_device_id: dev });
          break;
        case "help":
          setKeysSheet(true);
          break;
        case "escape":
          if (aloudOpen || keysSheet || scoring || scanOpen) return;
          setDeckOpen(false);
          setPosterOpen(false);
          setSearch("");
          focusStage();
          break;
      }
    },
    [aloudOpen, cmd, deck.next, dev, item?.revealed, keysSheet, openProjector, primaryAction, quiet, say, scanOpen, scoring, selected, setQuiet, setSkipped, setTimerS, skipped],
  );

  useLiveShortcuts({
    enabled: keysOn && !aloudOpen && !keysSheet && !scoring && !scanOpen,
    context,
    onAction: perform,
    onRefuse: say,
  });

  // --- the action bar ------------------------------------------------------------------------
  const notLive = live ? undefined : "live.refuse.notLive";
  const primary: BarAction | null = (() => {
    if (face === "loading") return null;
    if (face === "ended")
      return session.data?.released_at
        ? null
        : { id: "release", label: t("live.release"), onClick: () => release.mutate(), loading: release.isPending };
    if (open)
      return {
        id: "close",
        label: agg && present > 0 && agg.answered >= present ? t("live.stage.closeAll") : t("live.closeNow"),
        keys: ["Space"],
        disabled: !live,
        reason: notLive,
        onClick: primaryAction,
      };
    if (selectedItem)
      return {
        id: "ask",
        label: closed ? t("live.bar.askNextVerb") : t("live.bar.askVerb"),
        stem: selectedItem.item.stem,
        keys: ["Space"],
        disabled: !live,
        reason: notLive,
        loading: busyItemId === selectedItem.item.item_id,
        onClick: primaryAction,
      };
    return { id: "aloud", label: t("live.aloud.title"), keys: ["S"], disabled: !live, reason: notLive, onClick: () => setAloudOpen(true) };
  })();

  const deckToggle: BarAction = {
    id: "deck",
    label: deckOpen ? t("live.bar.closeDeck") : t("live.bar.deck"),
    keys: [deckOpen ? "Esc" : "↓"],
    onClick: () => {
      setDeckOpen(!deckOpen);
      if (deckOpen) focusStage();
    },
  };
  const roomToggle: BarAction = {
    id: "room",
    label: showRoom ? t("live.bar.roomHide") : t("live.bar.room"),
    keys: ["G"],
    onClick: () => setShowRoom((v) => !v),
  };
  /** Named for what it is. "Screen" told a teacher nothing — the owner had to ask what it did
   * (2026-09-11) — and the two cases are different acts: open the window, or bring it back. */
  const screen: BarAction = {
    id: "screen",
    label: projectorUp ? t("live.bar.projectorFocus") : t("live.bar.projectorOpen"),
    title: t("live.bar.projectorHint"),
    keys: ["P"],
    onClick: openProjector,
  };
  const quietToggle: BarAction = {
    id: "quiet",
    label: quiet ? t("live.quiet.show") : t("live.quiet.hide"),
    keys: ["Q"],
    onClick: () => setQuiet(!quiet),
  };

  const secondaries: BarAction[] = (() => {
    // Ended: only what is still honestly the teacher's — the projector, and reading back what
    // the class did. Everything that would command the engine is gone from the bar entirely.
    if (face === "ended") return [screen, deckToggle, roomToggle];
    if (open)
      return [
        { id: "extend", label: t("live.bar.addTime", { n: 30 }), keys: ["3"], disabled: !live, reason: notLive, onClick: () => cmd("extend", { seconds: 30 }) },
        // no shortcut: a photo is taken on the phone, which has no keyboard
        ...(scanOffered(cardsOn, capabilities.cardCamera, scanItem)
          ? [{ id: "cards", label: t("cards.scan.action"), disabled: !live, reason: notLive, onClick: () => setScanOpen(true) }]
          : []),
        roomToggle,
        screen,
        deckToggle,
        quietToggle,
      ];
    if (closed)
      return [
        {
          id: "reveal",
          label: item?.revealed === "correct" ? t("live.hideAnswer") : t("live.reveal.answer"),
          keys: ["A"],
          disabled: !live || !hasKey || (agg?.answered ?? 0) === 0,
          reason: !live ? notLive : (agg?.answered ?? 0) === 0 ? "live.refuse.noAnswers" : "live.refuse.noKey",
          onClick: () => cmd("reveal", { mode: item?.revealed === "correct" ? "nothing" : "correct" }),
        },
        {
          id: "shares",
          label: item?.revealed === "distribution" ? t("live.reveal.hideShares") : t("live.reveal.shares"),
          keys: ["D"],
          disabled: !live || (agg?.answered ?? 0) === 0,
          reason: !live ? notLive : "live.refuse.noAnswers",
          onClick: () => cmd("reveal", { mode: item?.revealed === "distribution" ? "nothing" : "distribution" }),
        },
        {
          id: "revote",
          label: t("live.revote"),
          keys: ["V"],
          disabled: !live || (agg?.answered ?? 0) === 0,
          reason: !live ? notLive : "live.refuse.noAnswers",
          onClick: () => perform({ kind: "revote" }),
        },
        deckToggle,
        roomToggle,
        screen,
        quietToggle,
      ];
    return [
      { id: "aloud", label: t("live.aloud.title"), keys: ["S"], disabled: !live, reason: notLive, onClick: () => setAloudOpen(true) },
      deckToggle,
      screen,
      roomToggle,
      quietToggle,
    ];
  })();

  const coach = refusal ?? (!tipDone && live && face !== "loading" ? "live.coach.space" : null);
  const stageHidden = deckOpen && !beside;
  /** Saying what the right answer was is the one act that outlives the end of the lesson: the
   * engine allows `score_item` until the results are released, so after the end the offer is
   * about the newest spoken round rather than about the one still on the stage. */
  const scoreTarget = (ended ? asked.find((a) => canScore(a)) : asked.find((a) => canScore(a) && canRevote(snapshot, a))) ?? null;
  /** Why the deck's acting controls are off, or null. All three are the engine's rules. */
  const deckBlocked = ended ? "live.refuse.ended" : open ? "live.refuse.closeFirst" : !live ? "live.refuse.notLive" : null;

  return (
    <TeacherChrome title={t("live.title")} wide course={course.data && courseId ? { id: courseId, title: course.data.title } : null} stage="live">
      <div
        className="live-scope flex flex-col gap-4"
        ref={(el) => {
          scope.current = el;
          setScopeEl(el);
        }}
      >
        {/* header: one line, always */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBar
              state={conn === "live" ? "live" : conn === "reconnecting" ? "reconnecting" : "offline"}
              detail={`${t("live.present", { count: present })}${(snapshot?.away ?? 0) > 0 ? ` · ${t("live.away", { count: snapshot?.away ?? 0 })}` : ""}`}
            />
            {session.data && present > 0 && (
              // `join_event` is refused once the session has ended, so the poster would be a
              // code nobody can use. The chip stays visible (the room may still be reading it
              // off the projector) and stops being a control.
              <Button
                variant="tertiary"
                disabled={ended}
                title={ended ? t("live.refuse.endedJoin") : undefined}
                onClick={() => setPosterOpen((v) => !v)}
              >
                <span className="num">{session.data.join_code}</span>
                <span className="sr-only">{ended ? t("live.refuse.endedJoin") : t("live.code.reopen")}</span>
              </Button>
            )}
            {!quiet && isLocked && <span className="text-sm text-[color:var(--ink-2)]">{t("live.lockedChip")}</span>}
            {!quiet && !iAmHost && <span className="text-sm text-[color:var(--ink-2)]">{t("live.otherIsHost")}</span>}
          </div>
          {/* A menu that stays open behind the lesson is a menu covering the deck: it closes
              on Escape and on the next touch anywhere else, and it never exceeds the phone. */}
          <details
            className="relative"
            ref={menu}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                menu.current?.removeAttribute("open");
                focusStage();
              }
            }}
          >
            <summary className="field inline-flex cursor-pointer list-none items-center px-4">{t("live.menu")}</summary>
            <div className="absolute right-0 z-20 mt-1 flex w-[min(16rem,calc(100vw-2rem))] flex-col gap-2 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-3 shadow-md">
              <Button variant="secondary" onClick={openProjector}>
                {projectorUp ? t("live.bar.projectorFocus") : t("live.bar.projectorOpen")}
              </Button>
              {/* Locking decides who may still join. After the end nobody may, whatever this
                  says, so it stops pretending to be a switch. */}
              <Button
                variant="secondary"
                disabled={ended}
                title={ended ? t("live.refuse.endedJoin") : undefined}
                onClick={() => lockSession(sessionId, !isLocked).then((s) => setLocked(s.locked))}
              >
                {isLocked ? t("live.unlock") : t("live.lock")}
              </Button>
              <Button variant="secondary" onClick={() => setKeysSheet(true)}>{t("live.keys.title")}</Button>
              {!iAmHost && (
                <Button
                  variant="secondary"
                  disabled={open || ended}
                  title={ended ? t("live.refuse.ended") : open ? t("live.handoffBetweenItems") : undefined}
                  onClick={() => cmd("handoff", { new_host_device_id: dev })}
                >
                  {t("live.takeOver")}
                </Button>
              )}
              <Button
                variant="destructive"
                disabled={ended}
                title={ended ? t("live.refuse.ended") : undefined}
                onClick={async () => {
                  if (await confirm({ title: t("live.endTitle"), body: t("live.endConfirm"), confirmLabel: t("live.end"), destructive: true }))
                    cmd("end");
                }}
              >
                {t("live.end")}
              </Button>
              {/* One line for the whole menu: a disabled control that cannot be hovered on a
                  phone still has to say why (the tooltip is not an answer). */}
              {ended && <p className="text-sm text-[color:var(--ink-2)]">{t("live.menuEnded")}</p>}
            </div>
          </details>
        </div>
        {last?.kind === "error" && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{String(last.data.detail)}</p>}
        {session.isError && <p role="alert" style={{ color: "var(--danger)" }}>{t("live.notFound")}</p>}

        <div className="live-split">
          <div className="live-stage-col flex flex-col gap-4">
            {stageHidden ? (
              // The deck took the column: a sticky three-line strip keeps the timer and the
              // result in view, so opening the list never loses the moment (§7.4).
              <button
                type="button"
                onClick={() => {
                  setDeckOpen(false);
                  focusStage();
                }}
                className="sticky top-0 z-10 flex min-h-[var(--control-h)] w-full items-center gap-2 rounded-[var(--radius-control)] bg-[color:var(--surface-2)] px-3 text-left text-sm"
              >
                <span aria-hidden>▲</span>
                <span className="min-w-0 flex-1 truncate">
                  {open
                    ? t("live.answered", { a: agg?.answered ?? 0, p: present })
                    : closed && agg
                      ? `${t("live.stage.closedWord")} · ${agg.scorable > 0 ? t("live.correct", { pct: Math.round(100 * (agg.correct_ratio ?? 0)) }) : t("live.done.noKey")}`
                      : t("live.stage.label")}
                </span>
              </button>
            ) : (
              <LiveStage
                face={face}
                snapshot={snapshot}
                upNext={
                  selectedItem
                    ? {
                        stem: selectedItem.item.stem,
                        lo: loCode(selectedItem.item.lo_id),
                        type: selectedItem.item.type,
                        draft: selectedItem.item.review_status === "draft",
                        step: selectedItem.segment,
                      }
                    : null
                }
                loCode={loCode}
                joinCode={session.data?.join_code ?? ""}
                joinUrl={joinUrl}
                skewMs={skewMs}
                quiet={quiet}
                dimmed={!live && state !== "ended"}
                telegram={session.data?.telegram ?? null}
                localHost={localHost}
              />
            )}

            {/* the decide strip: the recommended action after a close */}
            {face === "result" && item && (agg?.answered ?? 0) > 0 && (
              <SuggestionCard
                suggestions={snapshot?.suggestions ?? []}
                aggregate={agg}
                item={item.snapshot}
                quiet={quiet}
                onIntervene={(kind, extra) => cmd("intervene", { kind, ...extra, accepted_draft: draft ? { source: draft.source, explanation: draft.explanation } : null })}
                onRevote={() => perform({ kind: "revote" })}
                onShowProjector={() => cmd("reveal", { mode: "distribution" })}
                draft={draft ? { explanation: draft.explanation, misconception: draft.misconception ?? undefined, kind: draft.source === "cached" ? "cached" : "draft", scaffold: draft.scaffold } : null}
                onExplain={() => setQuiet(false)}
              />
            )}

            {/* post-hoc scoring of the round just closed: the offer sits where the teacher is */}
            {(face === "result" || face === "ended") && scoreTarget && !session.data?.released_at && (
              <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border border-dashed p-3 text-sm">
                <span className="min-w-0 flex-1">{t("live.score.offer")}</span>
                <Button variant="secondary" onClick={() => setScoring(scoreTarget)}>
                  {scoreTarget.keyed ? t("live.score.change") : t("live.score.cta")}
                </Button>
              </div>
            )}

            {face === "ended" && (
              <div className="flex flex-col gap-3 text-sm">
                {/* What is off and what is still yours, said once, where the teacher is
                    looking — a greyed control on a phone has no tooltip to hover. */}
                <p className="text-[color:var(--ink-2)]">
                  {session.data?.released_at ? t("live.endedWhatDone") : t("live.endedWhat")}
                </p>
                {session.data?.released_at ? <p>{t("live.released")}</p> : <p className="text-[color:var(--ink-2)]">{t("live.releaseHint")}</p>}
                <p className="flex flex-wrap gap-4">
                  <Link to="/t/live/$sessionId/summary" params={{ sessionId }} className="underline">{t("live.summary")}</Link>
                  {courseId && <Link to="/t/c/$courseId" params={{ courseId }} className="underline">{t("live.backToCourse")}</Link>}
                </p>
              </div>
            )}

            {face === "empty" && courseId && (
              <p className="text-sm">
                {/* The item bank is a route of this app, not an external page: a browser
                    can keep the live view in its own tab, but the desktop shell has no second
                    tab to open (`<a target="_blank">` does nothing there), so on the desktop
                    it navigates in place -- which costs nothing, the session having no items
                    to show. */}
                <Link to="/t/c/$courseId/items" params={{ courseId }} target={capabilities.opener.native ? undefined : "_blank"} className="underline">{t("live.addItems")}</Link>
              </p>
            )}

            <ActionBar primary={primary} secondaries={secondaries} onRefuse={say} />
            <RefusalLine message={coach} />
          </div>

          {(deckOpen || beside) && (
            <div className="flex flex-col gap-4">
              {deckOpen ? (
                <QuestionDeck
                  deck={deck}
                  tab={tab}
                  onTab={setTab}
                  selected={selected}
                  onSelect={setSelected}
                  loCode={loCode}
                  blocked={deckBlocked}
                  ended={ended}
                  busyItemId={busyItemId}
                  released={!!session.data?.released_at || !!snapshot?.released}
                  search={search}
                  onSearch={setSearch}
                  onAsk={askOrApprove}
                  onSkip={(id) => setSkipped([...skipped, id])}
                  onRestore={(id) => setSkipped(skipped.filter((s) => s !== id))}
                  onRevote={() => perform({ kind: "revote" })}
                  onAskAgain={askAgain}
                  onScore={setScoring}
                  onKeep={keep}
                  canRevote={(entry) => canRevote(snapshot, entry)}
                  kept={kept}
                  keeping={keeping}
                  channels={snapshot?.channels}
                />
              ) : (
                <section className="flex flex-col gap-2 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
                  <p className="text-sm text-[color:var(--ink-2)]">
                    {t("live.deck.next")} · {t("live.deck.count", { n: deck.next.length })}
                  </p>
                  <p className="clamp-3">{selectedItem?.item.stem ?? t("live.deck.emptyNext")}</p>
                  <Button variant="secondary" onClick={() => setDeckOpen(true)} aria-keyshortcuts="ArrowDown">
                    <span>{t("live.bar.deck")}</span>
                  </Button>
                </section>
              )}
              {showRoom && snapshot && (
                <RoomPanel
                  snapshot={snapshot}
                  ended={ended}
                  anonymous={anonymous}
                  onHide={() => setShowRoom(false)}
                  onLabel={(clusterId, correct) => cmd("label_answers", { cluster_id: clusterId, correct })}
                />
              )}
            </div>
          )}
        </div>

        {showRoom && snapshot && !beside && !deckOpen && (
          <RoomPanel snapshot={snapshot} ended={ended} anonymous={anonymous} onHide={() => setShowRoom(false)} onLabel={(clusterId, correct) => cmd("label_answers", { cluster_id: clusterId, correct })} />
        )}
      </div>

      {aloudOpen && course.data && (
        <AskAloudSheet
          los={course.data.los}
          defaultLoId={item?.snapshot.lo_id ?? asked[0]?.lo_id ?? null}
          defaultTimer={timerS}
          blocked={ended ? "live.refuse.ended" : open ? "live.aloud.blocked" : null}
          onClose={() => {
            setAloudOpen(false);
            focusStage();
          }}
          onAsk={askAloud}
        />
      )}
      {scoring && (
        <ScoreAloudSheet
          entry={scoring}
          onClose={() => {
            setScoring(null);
            focusStage();
          }}
          onScore={(keys) => {
            cmd("score_item", { item_id: scoring.item_id, attempt: scoring.attempt, correct_keys: keys });
            setScoring(null);
            focusStage();
          }}
        />
      )}
      {scanOpen && scan.ctl && (
        <CardScanSheet
          ctl={scan.ctl}
          canvas={scan.canvas}
          anonymous={anonymous}
          item={scanItem}
          onClose={() => {
            setScanOpen(false);
            focusStage();
          }}
        />
      )}
      {keysSheet && (
        <ShortcutSheet
          enabled={keysOn}
          onToggle={setKeysOn}
          onClose={() => {
            setKeysSheet(false);
            focusStage();
          }}
        />
      )}
    </TeacherChrome>
  );
}
